
import {of as observableOf,  Observable, of, tap, Subject, takeUntil } from 'rxjs';
import { Injectable, EventEmitter, inject } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { errorMessage } from '@nuso/common/util-errors';
import { Form } from '@nuso/forms/data-access';
import { FormDialogComponent, FormDialogData } from './form-dialog.component';

const DEFAULT_OPTIONS = {
  minWidth: '350px'
}

export class BaseDialogSaveEvent<T> {
  constructor(public data: T) {
  }

  busy(message: string) {
  }

  fail(message: unknown): Observable<boolean> {
    return of(false);
  }

  complete() {
  }
}

export class DialogSaveEvent<T> extends BaseDialogSaveEvent<T> {

  constructor(public dialog: FormDialog<T>, public override data: T) {
    super(data);
  }

  override busy(message: string) {
    return this.dialog.busy(message);
  }

  override fail(message: unknown, returnValue: any = false) {
    return this.dialog.fail(message, returnValue);
  }

  override complete() {
    return this.dialog.close();
  }
}


export class FormDialog<D> {
  public dialogSaving: EventEmitter<DialogSaveEvent<D>>;
  public formInit: EventEmitter<Form>;
  private _dialogClosed = new Subject<void>();
  public dialogClosed$ = this._dialogClosed.asObservable();
  private component: FormDialogComponent;

  public form: Form;

  constructor(public dialogRef: MatDialogRef<FormDialogComponent<D>>) {
    this.component = dialogRef.componentInstance;

    this.dialogSaving = new EventEmitter<DialogSaveEvent<D>>();
    dialogRef.componentInstance.formSubmitted
      .pipe(
        takeUntil(this.dialogClosed$)
      )
      .subscribe((d: D) => this.dialogSaving.emit(new DialogSaveEvent(this, d)));

    this.formInit = new EventEmitter<Form>();
    dialogRef.componentInstance.formInitiated
      .pipe(
        takeUntil(this.dialogClosed$)
      )
      .subscribe((f: Form) => {
        this.form = f;
        this.formInit.emit(f)
      });

    dialogRef.afterClosed().subscribe(() => {
      this._dialogClosed.next();
      this.dialogSaving.complete();
      this.formInit.complete();
      this.dialogSaving.complete();
    });
  }

  busy(message: string) {
    this.component.busyMessage = message;
    this.component.errorMessage = null;
    this.component.markChanged();
  }

  // Accepts the caught error itself; errorMessage keeps "ApolloError: " out of the dialog.
  fail(message: unknown, returnValue: any = false) {
    this.component.busyMessage = null;
    this.component.errorMessage = errorMessage(message);
    this.component.markChanged();
    return observableOf(returnValue);
  }

  close() {
    this.component.busyMessage = null;
    this.component.errorMessage = null;
    this.dialogRef.close();
  }

  // The dialog and its form view are both OnPush, so anything that fills a
  // field in after the dialog opens -- options fetched over the network, most
  // often -- is there but unrendered until some unrelated interaction happens
  // to trigger a check. Call this after mutating a field.
  markChanged() {
    this.component.markChanged();
  }
}

@Injectable()
export class FormDialogService {
  private dialog = inject(MatDialog);


  createDialog<D>(data: FormDialogData, config?: MatDialogConfig) {
    const dialogRef = <MatDialogRef<FormDialogComponent<D>, any>>this.dialog.open(FormDialogComponent, {
      ...(config || DEFAULT_OPTIONS),
      data,
      restoreFocus: false
    });

    return new FormDialog<D>(dialogRef);
  }
}
